import { nanoid } from "nanoid";
import { createContext, useCallback, useContext, useState } from "react";

export type ToastType = "success" | "error" | "info";

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  isExiting?: boolean; // Add this property
}

interface ToastContextType {
  toasts: Toast[];
  addToast: (message: string, type?: ToastType, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <button
          key={toast.id}
          type="button"
          style={{ borderRadius: "52px 100px 91px 99px / 61px 58px 66px 82px" }}
          className={`toast toast-${toast.type} ${toast.isExiting ? "exiting" : ""}`}
          onClick={() => onRemove(toast.id)}
        >
          {toast.message}
        </button>
      ))}
    </div>
  );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const FADE_DURATION_MS = 300; // Match CSS animation duration

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: ToastType = "info", duration = 3000) => {
      const id = nanoid();
      const newToast: Toast = { id, message, type, duration, isExiting: false }; // Initialize isExiting
      setToasts((prev) => [...prev, newToast]);

      if (duration > 0) {
        setTimeout(() => {
          // Mark toast as exiting after its visible duration
          setToasts((prev) =>
            prev.map((toast) => (toast.id === id ? { ...toast, isExiting: true } : toast)),
          );
          // Then, actually remove it after the fadeOut animation
          setTimeout(() => removeToast(id), FADE_DURATION_MS);
        }, duration);
      }
    },
    [removeToast],
  );

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
